import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { JournalLine } from '@finvox/shared/journal';
import { validateJournalBalance } from '@finvox/shared/journal';
import { Button } from '@/components/ui/Button';
import { IconButton } from '@/components/ui/IconButton';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { mapJournalBalanceError } from '@/lib/toastErrors';
import { cn } from '@/lib/cn';
import { Select } from '@/components/ui/Select';
import { useOrg } from '@/contexts/OrgContext';
import { apiPost } from '@/lib/api';
import { getFirestoreDb } from '@/lib/firebase';
import { useToast } from '@/contexts/ToastContext';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { PaginatedTable } from '@/components/ui/PaginatedTable';

type Template = {
  id: string;
  name: string;
  frequency: 'monthly' | 'weekly';
  dayOfMonth?: number;
  nextRunDate: string;
  active: boolean;
  memo?: string;
  lines: JournalLine[];
};

type Account = { id: string; code: string; name: string };

const emptyLine = (): JournalLine => ({ accountId: '', debit: '0.00', credit: '0.00' });

export function RecurringTemplates() {
  const { t } = useTranslation();
  const { orgId } = useOrg();
  const online = useOnlineStatus();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [name, setName] = useState('');

  const mapTemplate = useCallback((id: string, data: Record<string, unknown>) => {
    return {
      id,
      name: String(data.name ?? ''),
      frequency: (data.frequency === 'weekly' ? 'weekly' : 'monthly') as Template['frequency'],
      dayOfMonth: data.dayOfMonth ? Number(data.dayOfMonth) : 1,
      nextRunDate: String(data.nextRunDate ?? ''),
      active: Boolean(data.active),
      memo: data.memo ? String(data.memo) : undefined,
      lines: (data.lines ?? [emptyLine(), emptyLine()]) as JournalLine[]
    };
  }, []);

  const templatesPage = useFirestorePageQuery<Template>({
    orgId,
    collectionPath: 'recurringJournalTemplates',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc: mapTemplate
  });

  const templates = templatesPage.rows;

  const mapAccount = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      code: String(data.code ?? ''),
      name: String(data.name ?? '')
    }),
    []
  );

  const accountsPage = useFirestorePageQuery<Account>({
    orgId,
    collectionPath: 'accounts',
    orderByField: 'code',
    orderDirection: 'asc',
    mapDoc: mapAccount,
    pageSize: 50
  });

  const accounts = accountsPage.rows;

  const selected = templates.find((tm) => tm.id === selectedId) ?? null;
  const balance = selected ? validateJournalBalance(selected.lines) : null;

  async function addTemplate() {
    if (!orgId || !name.trim()) return;
    const today = new Date().toISOString().slice(0, 10);
    const ref = await addDoc(
      collection(getFirestoreDb(), 'organizations', orgId, 'recurringJournalTemplates'),
      {
        name: name.trim(),
        frequency: 'monthly',
        dayOfMonth: 1,
        lines: [emptyLine(), emptyLine()],
        memo: name.trim(),
        active: false,
        nextRunDate: today,
        createdAt: serverTimestamp()
      }
    );
    setName('');
    setSelectedId(ref.id);
    templatesPage.refresh();
    toast.success(t('recurring.created'), 'recurring');
  }

  async function saveTemplate(patch: Partial<Template>) {
    if (!orgId || !selected) return;
    await updateDoc(
      doc(getFirestoreDb(), 'organizations', orgId, 'recurringJournalTemplates', selected.id),
      { ...patch, updatedAt: serverTimestamp() }
    );
    toast.success(t('recurring.saved'), 'recurring');
  }

  async function runNow() {
    if (!orgId) return;
    const res = await apiPost<{ processed: number }>('/v1/recurring/run', { orgId });
    toast.success(t('recurring.ran', { count: res.processed }), 'recurring');
  }

  function updateLine(i: number, patch: Partial<JournalLine>) {
    if (!selected) return;
    const lines = selected.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l));
    void saveTemplate({ lines });
  }

  return (
    <section className="mt-8 rounded-lg border border-[var(--fv-border)] p-4">
      <h2 className="text-lg font-semibold">{t('recurring.title')}</h2>
      <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('recurring.subtitle')}</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <input
          className="rounded border px-3 py-2 text-sm"
          placeholder={t('recurring.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button variant="secondary" onClick={() => void addTemplate()} disabled={!online}>
          {t('recurring.add')}
        </Button>
        <Button onClick={() => void runNow()} disabled={!online}>
          {t('recurring.runNow')}
        </Button>
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <PaginatedTable
          loading={templatesPage.loading}
          pageIndex={templatesPage.pageIndex}
          hasNext={templatesPage.hasNext}
          hasPrev={templatesPage.hasPrev}
          onNext={templatesPage.nextPage}
          onPrev={templatesPage.prevPage}
          pageSize={templatesPage.pageSize}
          onPageSizeChange={templatesPage.setPageSize}
        >
          <ul className="divide-y divide-[var(--fv-border)] text-sm">
            {templates.map((tm) => (
              <li key={tm.id}>
                <Button
                  type="button"
                  variant="ghost"
                  fullWidth
                  className={cn(
                    'justify-start',
                    selectedId === tm.id && 'bg-[var(--fv-primary-muted)] text-[var(--fv-text)]'
                  )}
                  onClick={() => setSelectedId(tm.id)}
                >
                  {tm.name} — {tm.frequency} — {tm.nextRunDate}
                  {!tm.active && ` (${t('taxes.inactive')})`}
                </Button>
              </li>
            ))}
          </ul>
        </PaginatedTable>

        {selected && (
          <div className="space-y-3 text-sm">
            <label className="flex flex-col gap-1">
              {t('recurring.name')}
              <input
                className="rounded border px-2 py-1"
                value={selected.name}
                disabled={!online}
                onChange={(e) => void saveTemplate({ name: e.target.value })}
              />
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.active}
                disabled={!online}
                onChange={(e) => void saveTemplate({ active: e.target.checked })}
              />
              {t('recurring.active')}
            </label>
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="p-2">{t('coa.code')}</th>
                    <th className="p-2 text-end">{t('coa.debit')}</th>
                    <th className="p-2 text-end">{t('coa.credit')}</th>
                    <th className="p-2" />
                  </tr>
                </thead>
                <tbody>
                  {selected.lines.map((line, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-2">
                        <Select
                          className="w-full min-w-[10rem]"
                          value={line.accountId}
                          disabled={!online}
                          placeholder="—"
                          onValueChange={(id) => {
                            const acc = accounts.find((a) => a.id === id);
                            updateLine(i, { accountId: id, accountCode: acc?.code });
                          }}
                          options={[
                            { value: '', label: '—' },
                            ...accounts.map((a) => ({
                              value: a.id,
                              label: `${a.code} — ${a.name}`
                            }))
                          ]}
                        />
                      </td>
                      <td className="p-2">
                        <MoneyInput
                          className="w-full"
                          value={line.debit}
                          disabled={!online}
                          onValueChange={(debit) =>
                            updateLine(i, { debit: debit || '0.00', credit: '0.00' })
                          }
                        />
                      </td>
                      <td className="p-2">
                        <MoneyInput
                          className="w-full"
                          value={line.credit}
                          disabled={!online}
                          onValueChange={(credit) =>
                            updateLine(i, { credit: credit || '0.00', debit: '0.00' })
                          }
                        />
                      </td>
                      <td className="p-2">
                        {selected.lines.length > 2 && (
                          <IconButton
                            size="sm"
                            variant="danger"
                            aria-label={t('common.delete')}
                            onClick={() =>
                              void saveTemplate({
                                lines: selected.lines.filter((_, j) => j !== i)
                              })
                            }
                          >
                            ×
                          </IconButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              variant="secondary"
              disabled={!online}
              onClick={() => void saveTemplate({ lines: [...selected.lines, emptyLine()] })}
            >
              {t('journals.addLine')}
            </Button>
            {balance && !balance.valid && (
              <p className="text-[var(--fv-danger)]">{mapJournalBalanceError(balance.error)}</p>
            )}
            <Button
              variant="dangerSoft"
              size="sm"
              disabled={!online}
              onClick={() => {
                if (!orgId) return;
                void deleteDoc(
                  doc(
                    getFirestoreDb(),
                    'organizations',
                    orgId,
                    'recurringJournalTemplates',
                    selected.id
                  )
                );
                setSelectedId(null);
              }}
            >
              {t('common.delete')}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}
